import { getMbtiCharacterImage } from '@/data/mbtiCharacterImages';
import { getDiagnosisResultCta } from './ctaTexts';
import { CODE_TO_META } from './registry';
import type {
  DiagnosisAxisRow,
  DiagnosisResultContent,
  DiagnosisResultData,
  MbtiCode,
} from './types';

const AXIS_VALUE_LABELS = {
  P: 'P（計画）',
  S: 'S（即興）',
  R: 'R（のんびり）',
  A: 'A（アクティブ）',
  L: 'L（冒険グルメ）',
  C: 'C（安心安定）',
  E: 'E（体験）',
  Z: 'Z（癒し）',
} as const;

/**
 * 4軸コードから表示番号を算出（PALZ → 6）
 * 並び: P/S × R/A × L/C × E/Z の2進表現 + 1
 */
export function codeToNo(code: MbtiCode): number {
  const bits = [
    code[0] === 'P' ? 0 : 1,
    code[1] === 'R' ? 0 : 1,
    code[2] === 'L' ? 0 : 1,
    code[3] === 'E' ? 0 : 1,
  ];
  return (bits[0] << 3) | (bits[1] << 2) | (bits[2] << 1) | bits[3] + 1;
}

/** 4軸コードから軸の組み合わせ行を生成 */
export function axesFromCode(code: MbtiCode): DiagnosisAxisRow[] {
  return [
    { key: 'PvsS', value: AXIS_VALUE_LABELS[code[0] as 'P' | 'S'] },
    { key: 'RvsA', value: AXIS_VALUE_LABELS[code[1] as 'R' | 'A'] },
    { key: 'LvsC', value: AXIS_VALUE_LABELS[code[2] as 'L' | 'C'] },
    { key: 'EvsZ', value: AXIS_VALUE_LABELS[code[3] as 'E' | 'Z'] },
  ];
}

/** コンテンツ定義を画面用データに正規化 */
export function buildDiagnosisResult(content: DiagnosisResultContent): DiagnosisResultData {
  const meta = CODE_TO_META[content.code];
  return {
    ...content,
    no: content.no ?? codeToNo(content.code),
    name: content.name || meta.name,
    characterImage: content.characterImage ?? getMbtiCharacterImage(content.code),
    axes: content.axes ?? axesFromCode(content.code),
    cta: content.cta ?? getDiagnosisResultCta(content.code),
  };
}

const PLACEHOLDER = '（準備中）';

/** 未実装タイプ用の仮データ（プレビュー・実装中のみ） */
export function createStubResult(code: MbtiCode): DiagnosisResultData {
  const meta = CODE_TO_META[code];
  return buildDiagnosisResult({
    code,
    name: meta.name,
    catchphrase: PLACEHOLDER,
    rarity: 3,
    style: PLACEHOLDER,
    strengths: [{ lead: PLACEHOLDER, text: '' }],
    weaknesses: [{ lead: PLACEHOLDER, text: '' }],
    aaru: [{ icon: 'utensils', text: PLACEHOLDER }],
    manual: { good: PLACEHOLDER, ng: PLACEHOLDER, tips: PLACEHOLDER },
    compatibility: { good: PLACEHOLDER, conflict: PLACEHOLDER, tips: PLACEHOLDER },
    destinations: { domestic: PLACEHOLDER, overseas: PLACEHOLDER },
    cta: getDiagnosisResultCta(code),
  });
}
